import React, { useId, useState } from 'react'

import { Container } from 'styles/PageLayout'
import { FileUploadResult, ValidationMessage } from 'domain/DTO'
import { ListDiffDetails } from 'components/diff/ListDiffDetails'
import { getIdField, getListDiff } from 'components/diff/AssortmentDiff'
import { ListDiffSummary } from 'components/diff/ListDiffSummary'
import { ArrowDownIcon, ArrowUpIcon } from '../../icons'

const ShowDiffButton = ({ showDiff, toggleShowDiff }: { showDiff: boolean; toggleShowDiff: () => void }) => {
  const text = showDiff ? 'Hide diff' : 'Show complete diff'
  return (
    <button className="btn" onClick={() => toggleShowDiff()}>
      {text}
    </button>
  )
}

const MessagesExpandable = ({ title, messages }: { title: string; messages: ValidationMessage[] }) => {
  const [isOpen, setIsOpen] = useState(false)
  const id = useId()
  const accordionContentId = 'accordionContent' + useId()

  return (
    <div className="accordion w-10/12 m-1">
      <h2 className="text-md">
        <button
          id={id}
          type="button"
          className="relative "
          aria-expanded={isOpen}
          aria-controls={accordionContentId}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="flex items-center justify-between">
            <span className="flex-1 mr-md">
              {title} ({messages.length})
            </span>
            {isOpen ? <ArrowUpIcon key={title} /> : <ArrowDownIcon key={title} />}
          </span>
        </button>
      </h2>
      {isOpen && (
        <div
          id={accordionContentId}
          role="region"
          aria-labelledby="accordionTitle"
          className="pb-md px-lg w-full overflow-scroll"
          style={{ maxHeight: '20rem' }}
        >
          <ul>
            {messages.map((validationMessage, index) => (
              <li className="even:bg-grey-light" key={index}>
                {validationMessage.entityId && 'id ' + validationMessage.entityId + ' - '} {validationMessage.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

type Props = {
  result: FileUploadResult
  latestFileVersion: string | null
  onApply: () => Promise<void>
}

export const FileUploadResultDialog = ({ result, latestFileVersion, onApply }: Props) => {
  const [isApplying, setIsApplying] = useState(false)
  const [showDetails, setShowDetails] = useState(false)

  const generalWarnings = result.messages.filter((m) => m.severity === 'WARNING' && !m.entityId)
  const generalErrors = result.messages.filter((m) => m.severity === 'ERROR' && !m.entityId)
  const itemRelatedWarnings = result.messages.filter((m) => m.severity === 'WARNING' && m.entityId)
  const itemRelatedErrors = result.messages.filter((m) => m.severity === 'ERROR' && m.entityId)
  const scrollingClassNames = showDetails ? 'overflow-y-auto max-h-[80vh]' : ''
  return (
    <Container className={scrollingClassNames}>
      {result.stagedFile ? (
        <>
          <h2 className="text-lg mb-md">File upload result</h2>
          <span>
            {latestFileVersion
              ? `Comparing current version '${latestFileVersion}' to staged version '${result.stagedFile.parsedVersion}'`
              : `'Staged the first file for this file stream, version: ${result.stagedFile.parsedVersion}'`}
          </span>

          {showDetails ? (
            <ListDiffDetails
              diff={getListDiff(result.diff ?? {})}
              idField={getIdField(result.diff ?? {})}
              messages={result.messages}
            />
          ) : (
            <>
              <ListDiffSummary diff={getListDiff(result.diff ?? {})} messages={result.messages} />
              {generalErrors.length > 0 && (
                <MessagesExpandable title={'Show general error messages'} messages={generalErrors} />
              )}
              {itemRelatedErrors.length > 0 && (
                <MessagesExpandable title={'Show item related error messages'} messages={itemRelatedErrors} />
              )}
              {generalWarnings.length > 0 && (
                <MessagesExpandable title={'Show general warning messages'} messages={generalWarnings} />
              )}
              {itemRelatedWarnings.length > 0 && (
                <MessagesExpandable title={'Show item related warning messages'} messages={itemRelatedWarnings} />
              )}
            </>
          )}

          <div className={'flex justify-end items-center gap-1 mt-6 w-full pr-lg'}>
            <ShowDiffButton showDiff={showDetails} toggleShowDiff={() => setShowDetails(!showDetails)} />
            <button
              className="btn btn-primary-dark"
              style={{ display: 'inline-block' }}
              disabled={result.stagedFile.latestFileId === -1 || isApplying}
              onClick={() => {
                setIsApplying(true)
                onApply()
              }}
            >
              Apply
            </button>
          </div>
        </>
      ) : (
        <>
          {generalWarnings.length > 0 ? (
            <ul>
              <li>Warnings:</li>
              <ul>
                {generalWarnings.map((w) => (
                  <li key={w.message}>{w.message}</li>
                ))}
              </ul>
            </ul>
          ) : null}
          {generalErrors.length > 0 ? (
            <ul>
              <li>Errors:</li>
              <ul>
                {generalErrors.map((err) => (
                  <li key={err.message}>{err.message}</li>
                ))}
              </ul>
            </ul>
          ) : null}
        </>
      )}
    </Container>
  )
}
